import { useCallback, useEffect, useRef, useState } from "react";

// Slow on purpose. These queues grow from an event in a *different* browser
// (another outlet acting), so there is no signal this tab can observe.
const POLL_INTERVAL_MS = 120_000;

// visibilitychange fires on every tab/window switch, and these users work with
// two outlets side by side — without a floor, alt-tabbing alone polls the
// endpoint continuously.
const MIN_REFRESH_INTERVAL_MS = 30_000;

/**
 * Count of rows waiting on a paginated queue endpoint, for a drawer badge.
 *
 * Requests one row and reads pageInfo.totalItems. Refreshes on tab focus, on a
 * slow poll, and immediately when `changedEvent` is dispatched on window (use
 * that after acting on the queue in this tab). Issues no request while
 * `enabled` is false.
 *
 * @param {Object} options
 * @param {Object} options.api - outlet-scoped client from useOutletApi().
 * @param {string} options.url - queue endpoint.
 * @param {boolean} options.enabled
 * @param {string} options.changedEvent - window event name forcing a refresh.
 */
export const usePendingCount = ({ api, url, enabled, changedEvent }) => {
  const [count, setCount] = useState(0);
  const lastLoadedAtRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) {
      setCount(0);
      return;
    }

    try {
      const {
        data: { pageInfo },
      } = await api.get(url, { params: { page: 0, size: 1 } });
      setCount(pageInfo?.totalItems ?? 0);
    } catch {
      // A badge is decoration; it must never take the shell down or nag the
      // user with a snackbar on every poll.
    } finally {
      lastLoadedAtRef.current = Date.now();
    }
  }, [api, url, enabled]);

  const loadIfStale = useCallback(() => {
    if (Date.now() - lastLoadedAtRef.current < MIN_REFRESH_INTERVAL_MS) return;
    load();
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!enabled) return undefined;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") loadIfStale();
    };

    const interval = setInterval(refreshWhenVisible, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    // Not throttled: an action in this tab is a known change, not a guess.
    window.addEventListener(changedEvent, load);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener(changedEvent, load);
    };
  }, [enabled, changedEvent, load, loadIfStale]);

  return count;
};
